import React, { createContext, useContext, useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';

const PermissionsContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children, userRole }) => {
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userRole) {
      setPermissions(null);
      setLoading(false);
      return;
    }

    const fetchPermissions = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/user-roles/${userRole}/permissions`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        setPermissions(data);
      } catch (error) {
        console.error('Error fetching permissions:', error);
        setPermissions(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [userRole]);

  // Helper functions to check permissions
  const can = (category, action) => {
    if (!permissions || !permissions[category]) return false;
    return permissions[category][action] === true;
  };

  const getMaxDiscount = () => {
    if (!permissions || !permissions.orders) return 0;
    return permissions.orders.applyDiscount || 0;
  };

  const getDefaultScreen = () => {
    if (!permissions || !permissions.ui) return 'server';
    return permissions.ui.defaultScreen || 'server';
  };

  const getDefaultFilters = () => {
    if (!permissions || !permissions.ui) return ['all'];
    return permissions.ui.defaultFilters || ['all'];
  };

  const showAdminDashboard = () => {
    if (!permissions || !permissions.ui) return false;
    return permissions.ui.showAdminDashboard === true;
  };

  const showServerDashboard = () => {
    if (!permissions || !permissions.ui) return false;
    return permissions.ui.showServerDashboard === true;
  };

  const value = {
    permissions,
    loading,
    can,
    getMaxDiscount,
    getDefaultScreen,
    getDefaultFilters,
    showAdminDashboard,
    showServerDashboard
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
